import React, { useMemo } from "react";
import { Collapse } from "antd";
import { ArrowLeft, CircleQuestionMark } from "lucide-react";
import { useNavigate } from "react-router-dom";


const FAQ_DATA = [
  {
    id: "1",
    question: <b>What does this application do?</b>,
    answer: (
      <p>
        It lets users upload structured data, run ML-based anomaly detection,
        and view results on a web dashboard — no coding, no ML knowledge
        required.
      </p>
    ),
  },
  {
    id: "2",
    question: <b>Who should use this platform?</b>,
    answer: (
      <p>
        Business users, auditors, analysts, finance teams, and reviewers who
        want AI-driven insights without touching code.
      </p>
    ),
  },
  {
    id: "3",
    question: <b>What problem does it solve?</b>,
    answer: (
      <p>
        Manual analysis is slow and error-prone. This platform automates anomaly
        detection and presents results visually in minutes.
      </p>
    ),
  },
  {
    id: "4",
    question: <b>What file types can I upload?</b>,
    answer: (
      <div>
        <ul className="list-disc pl-5">
          <li>Excel (.xlsx, including multi-sheet workbooks) </li>
          <li>CSV files (.csv)</li>
          <li>Structured tabular data supported by the backend pipeline </li>
        </ul>
        <p>File size up to: <strong>25 MB</strong>.</p>
      </div>
    ),
  },
  {
    id: "5",
    question: <b>How does anomaly detection work?</b>,
    answer: (
      <div>
        <ol className="list-decimal pl-5">
          <li>User uploads dataset</li>
          <li>Backend preprocesses & applies ML models</li>
          <li>Frontend displays anomalies, stats, and charts</li>
        </ol>
        <p>No configuration gymnastics. </p>
      </div>
    ),
  },
  {
    id: "6",
    question: <b>Do I need ML or technical knowledge?</b>,
    answer: (
      <p>
        No — The UI is <b>designed for business users</b>, not data scientists.
      </p>
    ),
  },
  {
    id: "7",
    question: <b>What columns should I select as the dependent variable?</b>,
    answer: (
      <p>
        Only <b>numerical columns</b> should be selected as dependent
        variables.If you pick text or categorical data, results will be
        meaningless.{" "}
      </p>
    ),
  },
  {
    id: "8",
    question: <b>How are results presented?</b>,
    answer: (
      <div>
        <ol className="list-decimal pl-5">
          <li>Interactive tables </li>
          <li>Charts (ECharts)</li>
          <li>Processed datasets</li>
          <li>Model outputs and anomaly indicators</li>
        </ol>
        <p>Everything stays <b>inside the app</b> for validation.</p>
      </div>
    ),
  },
  {
    id: "9",
    question: <b>What happens if something goes wrong?</b>,
    answer: (
      <div>
        <ol className="list-decimal pl-5">
          <li>Invalid data → clear validation errors </li>
          <li>Model failure → toast alerts + retry option </li>
          <li>Job status → real-time loaders and async updates </li>
        </ol>
        <p>No silent failures.</p>
      </div>
    ),
  },
  {
    id: "10",
    question: <b>How is security handled?</b>,
    answer: (
      <div>
        <ol className="list-decimal pl-5">
          <li>JWT-based authentication</li>
          <li>Token refresh</li>
          <li>OTP / email verification</li>
          <li>Protected routes</li>
        </ol>
        <p>Your data doesn’t float around unsecured.</p>
      </div>
    ),
  },
];

export default function Help() {

  const navigate = useNavigate();

  const items = useMemo(
    () =>
      FAQ_DATA.map((item) => ({
        key: item.id,
        label: item.question,
        children: item.answer,
        "aria-label": `FAQ-${item.id}`,
      })),
    []
  );

  return (
    <div className="flex-1 overflow-auto ">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <header className="mb-6">
          <div className="flex justify-between items-start">
            {/* Left side: Icon + Title */}
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <CircleQuestionMark className="w-9 h-9 text-purple-500" />
              </div>
              <div>
                <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-500 via-violet-400 to-purple-300 bg-clip-text text-transparent">
                  Frequently Asked Questions
                </h2>
                <p className="text-violet-400 mt-1">
                  Find answers to common questions about XynexAI
                </p>
              </div>
            </div>

            {/* Right side: Back to Login button */}
            <button
              type="button" // Fixed: was incorrectly "text"
              onClick={() => navigate("/login")}
              className="flex items-center text-gray-300 hover:text-gray-200 font-medium transition-all duration-200 whitespace-nowrap"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </button>
          </div>
        </header>

        {/* FAQ Items */}
        <section className="space-y-4">
          <Collapse
            accordion
            defaultActiveKey={["1"]}
            items={items}
            className="bg-[#2D2D2D] border border-gray-700 rounded-lg [&_.ant-collapse-item]:bg-[#2D2D2D] [&_.ant-collapse-header]:bg-[#2D2D2D] [&_.ant-collapse-item]:border-b-[#2D2D2D] [&_.ant-collapse-content]:border-t-[#2D2D2D] [&_.ant-collapse-header]:!text-gray-100 [&_.ant-collapse-header]:text-lg [&_.ant-collapse-content]:bg-[#2D2D2D] [&_.ant-collapse-content-box]:text-gray-200 [&_.ant-collapse-content-box]:text-base [&_.ant-collapse-arrow]:text-gray-300"
          />
        </section>

        {/* Contact Section */}
        {/* <aside className="mt-8 p-6 bg-gradient-to-r from-purple-500 via-violet-400 to-purple-300 rounded-xl shadow-lg text-white">
          <h3 className="text-xl font-bold mb-2">Still have questions?</h3>
          <p className="mb-4">Our support team is here to help you 24/7</p>
          <button
            type="button"
            className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
          >
            Contact Support
          </button>
        </aside> */}
      </div>
    </div>
  );
}
 

